//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// suggestion-action-personal-cal imports
import { IContainer, IProps, IState } from "./SuggestionActionPersonalCal.d";
import { suggestionActionPersonalCalFragments } from "./SuggestionActionPersonalCal.gql";
import { styles, icon } from "./SuggestionActionPersonalCal.styles";
// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
// state
import { IReduxState } from "@reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";
// other components
import { DataSourceCreate } from "@components/data-source-create";
import { CardButton } from "@components/card";
// util
import { logger } from "@util/logger.util";
import { sortDataSources } from "@util/data-source.util";
import { enableDataSourceSync } from "@mutations";
import { track, TrackingEvents } from "@util/analytics.util";
// styles
// material-ui
import { EventAvailable } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// TYPES
//////////////////
export enum ViewState {
  Ready,
  PersonalAuthedNoSync,
  SyncEnabled,
}

//////////////////
// COMPONENT
//////////////////
class SuggestionActionPersonalCalCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = this.getStateFromProps(props);
  }

  // ~-~-~-~-~-~-~-
  // State Handling
  // ~-~-~-~-~-~-~-
  private getStateFromProps = (props: IProps): IState => {
    const { org, user } = props;
    // sort the data sources to check if a personal dataSource is already present
    const sortedDataSources = sortDataSources(user.dataSources.edges, org.domains);
    const hasPersonal = !!sortedDataSources.other && sortedDataSources.other.length > 0;
    const syncIsEnabled = this.syncIsEnabled(sortedDataSources.other);
    const personalCalId = this.getDataSourceCalId(sortedDataSources.other);

    let view = ViewState.Ready;

    if (syncIsEnabled) {
      view = ViewState.SyncEnabled;
    } else if (hasPersonal) {
      view = ViewState.PersonalAuthedNoSync;
    }

    return { view, personalCalId, saving: false };
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private getDataSourceCalId = (personalDataSources: ISchema.IDataSource[]) => {
    return (personalDataSources[0] && personalDataSources[0].name) || null;
  };

  private syncIsEnabled = (personalDataSources: ISchema.IDataSource[]) => {
    if (!personalDataSources || !personalDataSources.length) {
      return false;
    }
    const syncingDataSources = personalDataSources.filter((dataSource) => {
      const { syncTargets } = dataSource;
      return !!syncTargets && !!syncTargets.length;
    });
    return syncingDataSources.length > 0;
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onOpenSettings = () => {
    this.props.setSettingsDialogOpen(true);
    this.props.toggleExpanded("expandAccount");
  };

  private onStartSave = () => {
    this.setState({ saving: true });
  };

  private onError = (authError: ISchema.DataSourceAuthError) => {
    let msg = ``;
    switch (authError) {
      case ISchema.DataSourceAuthError.SameDomainError:
        msg = `Personal calendars cannot be on the same domain as your work calendar.`;
        this.props.snackBarOpen(msg);
        break;
      case ISchema.DataSourceAuthError.GenericError:
      default:
        msg = `There was a problem connecting this calendar as a personal calendar`;
        this.props.snackBarOpen(msg);
        logger.error(msg, authError);
        break;
    }

    this.setState({ saving: false });
  };

  private onCreateSuccess = (createPayload: ISchema.ICreateDataSourceMutationPayload) => {
    const personalCalId = createPayload.dataSource.name;
    const view = ViewState.PersonalAuthedNoSync;
    this.setState({ personalCalId, view });
    track(TrackingEvents.SETTINGS.DATA_SOURCE_ADD);

    // create automatically syncs via webserver now
    // this.enableSync(personalCalId);
    this.onSyncSuccess();
  };

  private enableSync = (sourceCalId: string) => {
    const { relay, org } = this.props;
    const orgRelayId = org.id;
    enableDataSourceSync(
      relay.environment,
      { orgRelayId, sourceCalId },
      this.onSyncSuccess,
      this.onError,
    );
  };

  private onSyncSuccess = () => {
    this.setState({
      view: ViewState.SyncEnabled,
      saving: false,
    });
    track(TrackingEvents.SETTINGS.DATA_SOURCE_SYNC_ON);
    this.props.snackBarOpen("Your personal calendar is now connected and syncing.");
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  // helper for assembling the buttons
  private renderBtn = (label: string, action: () => void, icon?: JSX.Element) => {
    return (
      <CardButton
        flat
        label={label}
        textColor="white"
        onClick={action}
        icon={icon}
        style={icon ? {} : { width: "inherit", display: "inline-block" }}
      />
    );
  };

  // success state — personal cal connected and syncing
  private renderSyncEnabled = () => {
    const dsName = this.state.personalCalId || "your calendar";
    return (
      <div>
        <div style={styles.syncing}>
          Syncing {dsName}. Events will take several minutes to appear.
        </div>
        {this.renderBtn("Done", this.props.actionCallback)}
        {this.renderBtn("Settings", this.onOpenSettings)}
      </div>
    );
  };

  // personal cal connected but not syncing yet
  private renderNeedsSync = (personalCalId: string) => {
    return (
      <div>
        <div style={styles.syncing}>{personalCalId} is connected but events are not syncing.</div>
        {this.renderBtn("Sync calendar", () => this.enableSync(personalCalId), <EventAvailable />)}
      </div>
    );
  };

  // personal cal not yet connected
  private renderAddPersonal = () => {
    const { height, width, ...styles } = icon;
    return (
      <DataSourceCreate
        org={this.props.org}
        isPersonalDataSource={true}
        onStartSave={() => this.onStartSave()}
        onSuccess={(payload) => this.onCreateSuccess(payload)}
        onError={this.onError}
        className={this.props.classes.button}
        disabled={this.state.saving}
        label={this.state.saving ? "Saving" : "Sync calendar"}
        labelStyle={{ paddingLeft: 8, marginBottom: -3 }}
        icon={
          <EventAvailable height={height} width={width} style={styles} htmlColor={greys.white} />
        }
        flat
      />
    );
  };

  // show relevent view state
  public render() {
    const { view, personalCalId } = this.state;

    switch (view) {
      case ViewState.SyncEnabled:
        return this.renderSyncEnabled();
      case ViewState.PersonalAuthedNoSync:
        if (!!personalCalId) {
          return this.renderNeedsSync(personalCalId);
        } else {
          return this.renderAddPersonal();
        }
      case ViewState.Ready:
      default:
        return this.renderAddPersonal();
    }
  }
}

//////////////////
// WITH STYLES
//////////////////
export const SuggestionActionPersonalCalStyled = withStyles(styles)(
  SuggestionActionPersonalCalCmpt,
);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const SuggestionActionPersonalCalRedux = connect(mapStateToProps, {
  setSettingsDialogOpen,
  toggleExpanded,
  snackBarOpen,
})(SuggestionActionPersonalCalStyled);

//////////////////
// RELAY
//////////////////
export const SuggestionActionPersonalCal = createFragmentContainer<IContainer>(
  SuggestionActionPersonalCalRedux,
  suggestionActionPersonalCalFragments,
);
